/**
 * The word games: Wordle, le Pendu, Anagrammes et la frappe rapide.
 *
 * The word lists are French and deliberately free of accents. Accents make the
 * on screen keyboard ambiguous (is E the same key as É?) and make typing a
 * guess on a phone a chore, and every one of these games is about the letters
 * rather than about the spelling of the diacritics.
 */
import { byLevel } from '@arena/games';

import { Lifecycle, h, randomOf, shuffle, tone } from '../kit.js';
import type { GameDef } from '../types.js';

/**
 * Five letter words for Motus, written without accents.
 *
 * Accents would make the on screen keyboard ambiguous (is E the same key as
 * E acute?) and turn every guess on a phone into a fight with the autocorrect.
 * A runtime filter enforces the length, because a four letter entry slipped in
 * here would break the grid rather than the list.
 */
const FIVES = [
  'ROUGE', 'VERTE', 'BLEUE', 'JAUNE', 'NOIRE', 'PORTE', 'TABLE', 'CHIEN', 'CHATS', 'PIANO',
  'LIVRE', 'FLEUR', 'ARBRE', 'PLAGE', 'NEIGE', 'PLUIE', 'ORAGE', 'NUAGE', 'LUNES', 'ROUTE',
  'VILLE', 'MONDE', 'TERRE', 'VERRE', 'MONTS', 'RIVES', 'BOIRE', 'MANGE', 'DORMI', 'COURT',
  'SAUTE', 'PARLE', 'ECRIT', 'LISEZ', 'JOUER', 'GAGNE', 'PERDU', 'LENTE', 'FORTE', 'DOUCE',
  'CHAUD', 'FROID', 'CLAIR', 'GRAND', 'PETIT', 'LARGE', 'RONDE', 'CARRE', 'POINT', 'LIGNE',
  'CUBES', 'BALLE', 'CARTE', 'DEFIS', 'SCORE', 'BONUS', 'PIEGE', 'CLEFS', 'TOURS', 'PONTS',
  'ROUES', 'AVION', 'TRAIN', 'VOILE', 'PECHE', 'FORET', 'CHAMP', 'HERBE', 'GRAIN', 'FRUIT',
  'POMME', 'POIRE', 'MELON', 'SUCRE', 'MIELS', 'PAINS', 'CREME', 'SAUCE', 'PATES', 'POULE',
  'BOEUF', 'LAPIN', 'TIGRE', 'LIONS', 'LOUPS', 'AIGLE', 'VACHE', 'SINGE', 'ZEBRE', 'HIBOU',
  'BRUME', 'GIVRE', 'GLACE', 'SABLE', 'ROCHE', 'CIELS', 'VENTS', 'BRISE', 'MARIN', 'PHARE',
  'QUAIS', 'DIGUE', 'CANAL', 'DENTS', 'LEVRE', 'JOUES', 'MAINS', 'PIEDS', 'COEUR', 'VESTE',
  'JUPES', 'BOTTE', 'GANTS', 'BAGUE', 'PERLE', 'LAMPE', 'BANCS', 'TOITS', 'CAVES', 'ALLEE',
  'HAIES', 'ROSES', 'LILAS', 'CHENE', 'SAPIN', 'HETRE', 'PALME', 'MERLE', 'CORNE', 'GUEPE',
  'DINDE', 'OISON', 'PONEY', 'MULET', 'LOUVE', 'OURSE', 'PANDA', 'KOALA', 'LAMAS', 'HYENE',
  'CARPE', 'MORUE', 'THONS', 'CRABE', 'MOULE', 'ALGUE', 'SPORT', 'MATCH', 'FILET', 'COURS',
  'SAUTS', 'LANCE', 'TIRER', 'VISER', 'VOLER', 'LUGES', 'PATIN', 'CRETE', 'COMBE', 'OASIS',
  'DUNES',
].filter((w, i, all) => w.length === 5 && all.indexOf(w) === i);

/** Longer words for the pendu and the anagram game. */
const WORDS = [
  'ORDINATEUR', 'MONTAGNE', 'CHOCOLAT', 'AVENTURE', 'BIBLIOTHEQUE', 'TELEPHONE',
  'MUSIQUE', 'JARDIN', 'FENETRE', 'VOYAGE', 'CUISINE', 'LUMIERE', 'PLANETE',
  'ETOILE', 'DRAGON', 'CHATEAU', 'FORTERESSE', 'TRESOR', 'PIRATE', 'ROBOT',
  'FUSEE', 'GALAXIE', 'DESERT', 'RIVIERE', 'OCEAN', 'TEMPETE', 'ORAGE',
  'BOUSSOLE', 'CARTOGRAPHE', 'LABYRINTHE', 'ENIGME', 'MYSTERE', 'SILENCE',
  'COURAGE', 'VICTOIRE', 'BATAILLE', 'BOUCLIER', 'EPEE', 'ARMURE', 'COURONNE',
  'ROYAUME', 'VILLAGE', 'MARCHE', 'BOULANGER', 'FROMAGE', 'BAGUETTE',
  // Long ones, added when difficulty shipped: the hard pendu and the hard
  // anagram both draw from the eight letters and up end of this list, and
  // seven words was a pool you memorised in an evening.
  'PARAPLUIE', 'ESCALIER', 'HORLOGE', 'MARMELADE', 'PAPILLON', 'CHAMPIGNON',
  'CROCODILE', 'ELEPHANT', 'KANGOUROU', 'DINOSAURE', 'ORCHESTRE', 'TROMPETTE',
  'CALENDRIER', 'DICTIONNAIRE', 'AMBULANCE', 'HELICOPTERE', 'SOUSMARIN',
  'PYRAMIDE', 'VOLCANIQUE', 'TELESCOPE', 'MICROSCOPE', 'ASTRONOMIE',
  'PRINTEMPS', 'AUTOMNE', 'BROUILLARD', 'ARCENCIEL', 'CASCADE', 'FALAISE',
];

/**
 * Short sentences, for the easy typing test.
 *
 * Short matters more than simple: the run ends when the sentence does, so a
 * shorter one is a shorter commitment and far less punishing to restart.
 */
const SHORT_SENTENCES = [
  'le chat dort sur le vieux tapis rouge',
  'la pluie tombe doucement sur les toits',
  'il ouvre la porte et allume la lampe',
  'nous partons demain matin par le train',
  'le pain chaud sent tres bon ce matin',
  'elle range ses livres sur la grande table',
];

/**
 * Long sentences, for the hard typing test.
 *
 * Longer, and deliberately full of the pairs that break a rhythm: doubled
 * letters, awkward hand alternations and words nobody types often.
 */
const LONG_SENTENCES = [
  'la bibliotheque municipale conserve quelques manuscrits que personne ne consulte jamais vraiment',
  'apres avoir longuement hesite il decida finalement que la meilleure solution restait la plus simple',
  'chaque nouvelle difficulte rencontree pendant le trajet transformait le voyage en veritable aventure',
  'les scientifiques expliquent que ce phenomene naturel se produit exclusivement pendant certaines saisons',
  'personne ne comprenait pourquoi cette vieille horloge continuait obstinement a sonner treize coups',
  'il fallut plusieurs semaines pour que les habitants acceptent enfin ce changement inattendu',
];

/** Sentences for the typing test, chosen to use common letter pairs. */
const SENTENCES = [
  'le vent souffle sur la plaine et fait danser les hautes herbes',
  'un petit chat noir traverse la rue sans regarder derriere lui',
  'la lumiere du matin entre par la fenetre et reveille la maison',
  'nous avons marche longtemps avant de trouver le vieux pont de pierre',
  'chaque partie commence par une decision et finit par une surprise',
  'il faut beaucoup de patience pour construire quelque chose de solide',
];

function pill(label: string, value: string): [HTMLElement, HTMLElement] {
  const box = h('div', 'ac-pill');
  const strong = h('strong', '', value);
  box.append(h('span', '', label), strong);
  return [box, strong];
}

// -------------------------------------------------------------------- wordle

export const wordle: GameDef = {
  id: 'wordle',
  title: 'Motus',
  blurb: 'Trouve le mot de cinq lettres en six essais.',
  category: 'Mots',
  difficulty: 'Difficile',
  icon: '\u{1F520}',
  unit: 'essais',
  higherIsBetter: false,
  how: 'Vert: bonne lettre bien placee. Jaune: bonne lettre mal placee. Gris: lettre absente.',
  mount(root, ctx) {
    const life = new Lifecycle();
    /*
     * Difficulty is how many guesses you get.
     *
     * Not the word list and not the colours. Making the words rarer would turn
     * it into a vocabulary test, and taking the yellows away would be a
     * different puzzle entirely. The number of attempts changes how much you
     * can afford to probe before you have to commit, which IS the game.
     */
    const ROWS = byLevel(ctx.level, 8, 6, 4);
    const LEN = 5;

    let answer = randomOf(FIVES);
    let guesses: string[] = [];
    let current = '';
    let done = false;

    const grid = h('div');
    // Bounded by viewport height as well as width, so the keyboard underneath
    // stays on screen instead of falling below the fold on a laptop.
    grid.style.cssText = 'display:grid;gap:6px;width:100%;max-width:min(330px,40vh)';
    const rows: HTMLElement[][] = [];
    for (let r = 0; r < ROWS; r++) {
      const row = h('div');
      row.style.cssText = `display:grid;grid-template-columns:repeat(${LEN},1fr);gap:6px`;
      const tiles: HTMLElement[] = [];
      for (let c = 0; c < LEN; c++) {
        const tile = h('div');
        tile.style.cssText =
          'aspect-ratio:1/1;display:grid;place-items:center;border:2px solid #2a1c47;' +
          'border-radius:8px;font-size:clamp(18px,6vw,28px);font-weight:800;text-transform:uppercase';
        tiles.push(tile);
        row.append(tile);
      }
      rows.push(tiles);
      grid.append(row);
    }

    /**
     * Score a guess the way the original does, which is subtler than it looks.
     *
     * A letter can only be marked yellow as many times as it actually occurs in
     * the answer, and exact matches claim their letter first. Without the two
     * pass count, guessing ELLES against a word with one L marks both L yellow
     * and quietly lies to the player.
     */
    const scoreGuess = (guess: string): Array<'hit' | 'near' | 'miss'> => {
      const marks: Array<'hit' | 'near' | 'miss'> = new Array(LEN).fill('miss');
      const pool: Record<string, number> = {};
      for (let i = 0; i < LEN; i++) {
        if (guess[i] === answer[i]) marks[i] = 'hit';
        else pool[answer[i]] = (pool[answer[i]] ?? 0) + 1;
      }
      for (let i = 0; i < LEN; i++) {
        if (marks[i] === 'hit') continue;
        const remaining = pool[guess[i]] ?? 0;
        if (remaining > 0) {
          marks[i] = 'near';
          pool[guess[i]] = remaining - 1;
        }
      }
      return marks;
    };

    const keyState = new Map<string, 'hit' | 'near' | 'miss'>();

    const paint = (): void => {
      for (let r = 0; r < ROWS; r++) {
        const word = guesses[r] ?? (r === guesses.length ? current : '');
        const settled = r < guesses.length;
        const marks = settled ? scoreGuess(guesses[r]) : null;
        for (let c = 0; c < LEN; c++) {
          const tile = rows[r][c];
          tile.textContent = word[c] ?? '';
          if (!settled) {
            tile.style.background = 'transparent';
            tile.style.borderColor = word[c] ? '#8b5cf6' : '#2a1c47';
            tile.style.color = '#f4f0ff';
            continue;
          }
          const m = marks![c];
          tile.style.background =
            m === 'hit' ? '#15803d' : m === 'near' ? '#a16207' : '#241539';
          tile.style.borderColor = 'transparent';
          tile.style.color = '#fff';
        }
      }
      for (const [letter, mark] of keyState) {
        const btn = keyButtons.get(letter);
        if (!btn) continue;
        btn.style.background = mark === 'hit' ? '#15803d' : mark === 'near' ? '#a16207' : '#1b1230';
        btn.style.color = mark === 'miss' ? '#6b5b8a' : '#fff';
      }
    };

    const submit = (): void => {
      if (done || current.length !== LEN) return;
      const marks = scoreGuess(current);
      marks.forEach((m, i) => {
        const letter = current[i];
        const prev = keyState.get(letter);
        // Never downgrade: a letter proven green must not go grey later.
        if (prev === 'hit') return;
        if (prev === 'near' && m === 'miss') return;
        keyState.set(letter, m);
      });
      guesses.push(current);
      const won = current === answer;
      current = '';
      paint();

      if (won) {
        done = true;
        ctx.submit(guesses.length);
        note.textContent = `Trouve en ${guesses.length} essai${guesses.length > 1 ? 's' : ''}.`;
        tone(880, 160, 'triangle');
      } else if (guesses.length >= ROWS) {
        done = true;
        note.textContent = `Perdu. Le mot etait ${answer}.`;
        tone(150, 300, 'square');
      } else {
        tone(440, 60);
      }
    };

    const type = (letter: string): void => {
      if (done || current.length >= LEN) return;
      current += letter;
      paint();
    };

    const back = (): void => {
      if (done) return;
      current = current.slice(0, -1);
      paint();
    };

    // On screen keyboard, because typing accented French on a phone keyboard
    // into a game like this is miserable.
    const keyboard = h('div');
    keyboard.style.cssText = 'display:flex;flex-direction:column;gap:5px;width:100%;max-width:440px';
    const keyButtons = new Map<string, HTMLElement>();
    for (const line of ['AZERTYUIOP', 'QSDFGHJKLM', 'WXCVBN']) {
      const row = h('div');
      row.style.cssText = 'display:flex;gap:5px;justify-content:center';
      if (line === 'WXCVBN') {
        const enter = h('button', 'ac-ghost', 'OK');
        enter.style.cssText = 'padding:10px 12px;font-size:13px;flex:1.4';
        enter.addEventListener('click', submit);
        row.append(enter);
      }
      for (const letter of line) {
        const key = h('button', '', letter);
        key.style.cssText =
          'flex:1;padding:11px 0;border-radius:7px;border:1px solid #2a1c47;background:#1b1230;' +
          'color:#f4f0ff;font-weight:700;font-size:14px;cursor:pointer;min-width:0';
        key.addEventListener('click', () => type(letter));
        keyButtons.set(letter, key);
        row.append(key);
      }
      if (line === 'WXCVBN') {
        const del = h('button', 'ac-ghost', '←');
        del.style.cssText = 'padding:10px 12px;font-size:15px;flex:1.4';
        del.addEventListener('click', back);
        row.append(del);
      }
      keyboard.append(row);
    }

    life.on(window, 'keydown', (ev: KeyboardEvent) => {
      if (ev.key === 'Enter') submit();
      else if (ev.key === 'Backspace') back();
      else if (/^[a-zA-Z]$/.test(ev.key)) type(ev.key.toUpperCase());
      else return;
      ev.preventDefault();
    });

    const TRIES = `${ROWS} essais. Moins tu en utilises, meilleur est ton score.`;
    const note = h('p', 'ac-note', TRIES);
    const again = h('button', 'ac-ghost', 'Nouveau mot');
    again.addEventListener('click', () => {
      answer = randomOf(FIVES);
      guesses = [];
      current = '';
      done = false;
      keyState.clear();
      for (const btn of keyButtons.values()) {
        btn.style.background = '#1b1230';
        btn.style.color = '#f4f0ff';
      }
      note.textContent = TRIES;
      paint();
    });

    paint();
    root.append(grid, note, keyboard, again);
    return () => life.stop();
  },
};

// --------------------------------------------------------------------- pendu

export const hangman: GameDef = {
  id: 'hangman',
  title: 'Le Pendu',
  blurb: 'Devine le mot lettre par lettre.',
  category: 'Mots',
  difficulty: 'Moyen',
  icon: '\u{1FA9D}',
  unit: 'erreurs',
  higherIsBetter: false,
  how: 'Sept erreurs autorisees. Le score est le nombre d’erreurs, donc zero est parfait.',
  mount(root, ctx) {
    const life = new Lifecycle();
    const MAX = 7;

    /*
     * Difficulty is the word you are given, not the number of errors allowed.
     *
     * The score IS the error count, so moving the allowance would change what a
     * score means rather than how hard it is to get. What changes instead is
     * the pool: short common words on easy, the long ones on hard, where every
     * unguessed letter is another chance to be wrong. Easy also opens with one
     * letter already showing, which is what turns a blank row of dashes into
     * something a child can start reasoning about.
     */
    const POOL = WORDS.filter((w) =>
      ctx.level === 'facile' ? w.length <= 7 : ctx.level === 'difficile' ? w.length >= 9 : true,
    );
    const FREE_LETTER = ctx.level === 'facile';

    // Replaced immediately by deal(), which is what applies the easy hint.
    let word = randomOf(POOL);
    let found = new Set<string>();
    let wrong = new Set<string>();
    let done = false;

    const [errBox, errValue] = pill('Erreurs', `0 / ${MAX}`);
    const hud = h('div', 'ac-hud');
    hud.append(errBox);

    const gallows = h('canvas') as HTMLCanvasElement;
    gallows.width = 220;
    gallows.height = 220;
    gallows.style.cssText = 'width:200px;height:200px';
    const gg = gallows.getContext('2d')!;

    const display = h('p', 'ac-big', '');
    display.style.cssText = 'letter-spacing:8px;font-size:clamp(22px,6vw,38px);margin:6px 0';

    const drawGallows = (): void => {
      gg.clearRect(0, 0, 220, 220);
      gg.strokeStyle = '#8b5cf6';
      gg.lineWidth = 4;
      gg.lineCap = 'round';
      const parts: Array<() => void> = [
        () => {
          gg.beginPath();
          gg.moveTo(20, 205);
          gg.lineTo(140, 205);
          gg.stroke();
        },
        () => {
          gg.beginPath();
          gg.moveTo(55, 205);
          gg.lineTo(55, 20);
          gg.stroke();
        },
        () => {
          gg.beginPath();
          gg.moveTo(55, 20);
          gg.lineTo(140, 20);
          gg.lineTo(140, 45);
          gg.stroke();
        },
        () => {
          gg.strokeStyle = '#ff2d95';
          gg.beginPath();
          gg.arc(140, 65, 20, 0, Math.PI * 2);
          gg.stroke();
        },
        () => {
          gg.beginPath();
          gg.moveTo(140, 85);
          gg.lineTo(140, 145);
          gg.stroke();
        },
        () => {
          gg.beginPath();
          gg.moveTo(140, 100);
          gg.lineTo(115, 125);
          gg.moveTo(140, 100);
          gg.lineTo(165, 125);
          gg.stroke();
        },
        () => {
          gg.beginPath();
          gg.moveTo(140, 145);
          gg.lineTo(118, 180);
          gg.moveTo(140, 145);
          gg.lineTo(162, 180);
          gg.stroke();
        },
      ];
      for (let i = 0; i < Math.min(wrong.size, parts.length); i++) parts[i]();
    };

    const paint = (): void => {
      display.textContent = [...word].map((c) => (found.has(c) ? c : '_')).join(' ');
      errValue.textContent = `${wrong.size} / ${MAX}`;
      drawGallows();
      for (const [letter, btn] of keys) {
        const used = found.has(letter) || wrong.has(letter);
        (btn as HTMLButtonElement).disabled = used || done;
        btn.style.opacity = used ? '0.32' : '1';
        btn.style.background = found.has(letter) ? '#15803d' : wrong.has(letter) ? '#7f1d1d' : '#1b1230';
      }
    };

    const guess = (letter: string): void => {
      if (done || found.has(letter) || wrong.has(letter)) return;
      if (word.includes(letter)) {
        found.add(letter);
        tone(640, 55, 'triangle');
        if ([...word].every((c) => found.has(c))) {
          done = true;
          ctx.submit(wrong.size);
          note.textContent = `Trouve avec ${wrong.size} erreur${wrong.size > 1 ? 's' : ''}.`;
        }
      } else {
        wrong.add(letter);
        tone(200, 90, 'square');
        if (wrong.size >= MAX) {
          done = true;
          note.textContent = `Perdu. Le mot etait ${word}.`;
          found = new Set(word);
        }
      }
      paint();
    };

    const keys = new Map<string, HTMLElement>();
    const keyboard = h('div');
    keyboard.style.cssText =
      'display:grid;grid-template-columns:repeat(auto-fit,minmax(34px,1fr));gap:5px;width:100%;max-width:440px';
    for (const letter of 'ABCDEFGHIJKLMNOPQRSTUVWXYZ') {
      const key = h('button', '', letter) as HTMLButtonElement;
      key.style.cssText =
        'padding:10px 0;border-radius:7px;border:1px solid #2a1c47;background:#1b1230;' +
        'color:#f4f0ff;font-weight:700;cursor:pointer';
      key.addEventListener('click', () => guess(letter));
      keys.set(letter, key);
      keyboard.append(key);
    }

    life.on(window, 'keydown', (ev: KeyboardEvent) => {
      if (/^[a-zA-Z]$/.test(ev.key)) guess(ev.key.toUpperCase());
    });

    const HOW = FREE_LETTER
      ? 'Sept erreurs maximum. Une lettre est offerte.'
      : 'Sept erreurs maximum.';
    const note = h('p', 'ac-note', HOW);
    const again = h('button', 'ac-ghost', 'Nouveau mot');

    /**
     * Start a word.
     *
     * On easy one letter is given away, chosen from the letters the word
     * actually contains, so a beginner never faces a row of nothing. It costs
     * no error and so cannot flatter the score, which counts mistakes.
     */
    const deal = (): void => {
      word = randomOf(POOL);
      found = new Set();
      wrong = new Set();
      done = false;
      if (FREE_LETTER) found.add(randomOf([...new Set(word)]));
      note.textContent = HOW;
      paint();
    };
    again.addEventListener('click', deal);

    deal();
    root.append(hud, gallows, display, keyboard, note, again);
    return () => life.stop();
  },
};

// ---------------------------------------------------------------- anagrammes

export const anagram: GameDef = {
  id: 'anagram',
  title: 'Anagrammes',
  blurb: 'Remets les lettres dans le bon ordre.',
  category: 'Mots',
  difficulty: 'Moyen',
  icon: '\u{1F524}',
  unit: 'points',
  higherIsBetter: true,
  how: 'Soixante secondes. Chaque mot retrouve rapporte des points, et un mot long en rapporte plus.',
  mount(root, ctx) {
    const life = new Lifecycle();
    const [scoreBox, scoreValue] = pill('Score', '0');
    const [timeBox, timeValue] = pill('Temps', '60');
    const hud = h('div', 'ac-hud');
    hud.append(scoreBox, timeBox);

    const scrambled = h('p', 'ac-big', '');
    scrambled.style.cssText = 'letter-spacing:6px;font-size:clamp(24px,7vw,44px);margin:10px 0';

    const input = h('input') as HTMLInputElement;
    input.type = 'text';
    input.autocomplete = 'off';
    input.autocapitalize = 'characters';
    input.placeholder = 'Ton mot';
    input.style.cssText =
      'padding:12px 16px;border-radius:11px;border:1px solid #2a1c47;background:#100a1e;' +
      'color:#f4f0ff;font-size:19px;text-align:center;text-transform:uppercase;width:100%;max-width:320px';

    /*
     * Difficulty is the length of the words and how thoroughly they are mixed.
     *
     * The sixty seconds are the same everywhere, because the score is how many
     * you got through and a longer clock would be a bigger number rather than
     * an easier game. On easy the first letter is left in place, which is a far
     * bigger hint than it sounds: it fixes the one position people anchor on.
     * On hard nothing stays where it started, so there is no free foothold.
     */
    const POOL = WORDS.filter((w) =>
      ctx.level === 'facile' ? w.length <= 7 : ctx.level === 'difficile' ? true : w.length <= 10,
    );
    const KEEPS_FIRST = ctx.level === 'facile';
    const DERANGED = ctx.level === 'difficile';

    let word = '';
    let score = 0;
    let endsAt = 0;
    let running = false;

    const nextWord = (): void => {
      word = randomOf(POOL);
      let mix = word;
      if (KEEPS_FIRST) {
        // Anchor the opening letter and shuffle the rest.
        for (let i = 0; i < 8 && mix.slice(1) === word.slice(1); i++) {
          mix = word[0] + shuffle([...word.slice(1)]).join('');
        }
      } else {
        // Reshuffle until it actually differs, or the puzzle is sometimes free.
        for (let i = 0; i < 8 && mix === word; i++) mix = shuffle([...word]).join('');
        if (DERANGED) {
          // Nothing left in its original position. Tried a bounded number of
          // times rather than forced, because a word with repeated letters can
          // make a full derangement impossible and a loop here would hang.
          for (let i = 0; i < 24; i++) {
            if (![...mix].some((c, j) => c === word[j])) break;
            mix = shuffle([...word]).join('');
          }
        }
      }
      scrambled.textContent = mix;
    };

    const start = (): void => {
      running = true;
      score = 0;
      scoreValue.textContent = '0';
      endsAt = performance.now() + 60_000;
      nextWord();
      input.value = '';
      input.focus();
      startBtn.hidden = true;
    };

    life.on(input, 'keydown', (ev: Event) => {
      const e = ev as KeyboardEvent;
      if (e.key !== 'Enter' || !running) return;
      const tried = input.value.trim().toUpperCase();
      input.value = '';
      if (tried === word) {
        score += 10 + word.length * 2;
        scoreValue.textContent = String(score);
        tone(760, 70, 'triangle');
        nextWord();
      } else {
        tone(190, 90, 'square');
        note.textContent = 'Pas celui la.';
      }
    });

    const skip = h('button', 'ac-ghost', 'Passer');
    skip.addEventListener('click', () => {
      if (!running) return;
      // Skipping costs points, otherwise it is a free way to hunt easy words.
      score = Math.max(0, score - 5);
      scoreValue.textContent = String(score);
      nextWord();
      input.focus();
    });

    life.frame(() => {
      if (!running) return;
      const left = Math.max(0, (endsAt - performance.now()) / 1000);
      timeValue.textContent = String(Math.ceil(left));
      if (left <= 0) {
        running = false;
        ctx.submit(score);
        note.textContent = `Termine. ${score} points.`;
        startBtn.hidden = false;
        startBtn.textContent = 'Rejouer';
      }
    });

    const note = h('p', 'ac-note', 'Tape le mot puis Entree.');
    const startBtn = h('button', 'ac-primary', 'Commencer');
    startBtn.addEventListener('click', start);

    const row = h('div');
    row.style.cssText = 'display:flex;gap:10px;align-items:center;flex-wrap:wrap;justify-content:center';
    row.append(input, skip);

    root.append(hud, scrambled, row, note, startBtn);
    return () => life.stop();
  },
};

// ------------------------------------------------------------- frappe rapide

export const speedTyping: GameDef = {
  id: 'speed-typing',
  title: 'Frappe rapide',
  blurb: 'Recopie la phrase le plus vite possible.',
  category: 'Mots',
  difficulty: 'Moyen',
  icon: '⌨',
  unit: 'mpm',
  higherIsBetter: true,
  how: 'Le chrono demarre a la première touche. Le score est en mots par minute, penalise par les fautes.',
  mount(root, ctx) {
    const life = new Lifecycle();
    const [wpmBox, wpmValue] = pill('MPM', '0');
    const [accBox, accValue] = pill('Precision', '100 %');
    const hud = h('div', 'ac-hud');
    hud.append(wpmBox, accBox);

    const target = h('p');
    target.style.cssText =
      'font-size:clamp(16px,3.4vw,22px);line-height:1.7;max-width:620px;text-align:center;' +
      'font-family:ui-monospace,SFMono-Regular,Menlo,monospace;margin:6px 0';

    const input = h('textarea') as HTMLTextAreaElement;
    input.rows = 3;
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.style.cssText =
      'width:100%;max-width:620px;padding:12px 14px;border-radius:11px;border:1px solid #2a1c47;' +
      'background:#100a1e;color:#f4f0ff;font-size:17px;font-family:ui-monospace,Menlo,monospace;resize:none';

    /*
     * Difficulty is the sentence, and how hard a typo hits.
     *
     * Accuracy already multiplies the raw speed. Squaring it on hard makes a
     * 90% run worth 81% rather than 90%, so hurrying past mistakes stops being
     * the fastest route; taking the square root on easy does the opposite and
     * keeps a beginner's score from collapsing over a few stray letters.
     */
    const POOL = byLevel(ctx.level, SHORT_SENTENCES, SENTENCES, LONG_SENTENCES);
    const ACCURACY_BITE = byLevel(ctx.level, 0.5, 1, 2);

    let sentence = randomOf(POOL);
    let startedAt = 0;
    let done = false;

    const paint = (): void => {
      const typed = input.value;
      target.replaceChildren();
      for (let i = 0; i < sentence.length; i++) {
        const span = h('span', '', sentence[i]);
        if (i < typed.length) {
          span.style.color = typed[i] === sentence[i] ? '#a3e635' : '#ff2d95';
          if (typed[i] !== sentence[i]) span.style.background = 'rgba(255,45,149,0.18)';
        } else {
          span.style.color = i === typed.length ? '#facc15' : '#a394c8';
          if (i === typed.length) span.style.borderBottom = '2px solid #facc15';
        }
        target.append(span);
      }
    };

    const finish = (): void => {
      done = true;
      const minutes = (performance.now() - startedAt) / 60_000;
      const typed = input.value;
      let correct = 0;
      for (let i = 0; i < sentence.length; i++) if (typed[i] === sentence[i]) correct++;
      const accuracy = correct / sentence.length;
      // Standard convention: a "word" is five characters. Scaling by accuracy
      // stops a fast typist scoring well by producing nonsense.
      const wpm = Math.round(
        (sentence.length / 5 / Math.max(minutes, 0.001)) * accuracy ** ACCURACY_BITE,
      );
      wpmValue.textContent = String(wpm);
      accValue.textContent = `${Math.round(accuracy * 100)} %`;
      input.disabled = true;
      note.textContent = `${wpm} mots par minute a ${Math.round(accuracy * 100)} % de precision.`;
      ctx.submit(wpm);
      tone(820, 140, 'triangle');
    };

    life.on(input, 'input', () => {
      if (done) return;
      if (!startedAt) startedAt = performance.now();
      paint();
      const typed = input.value;
      let correct = 0;
      for (let i = 0; i < Math.min(typed.length, sentence.length); i++) {
        if (typed[i] === sentence[i]) correct++;
      }
      accValue.textContent = `${Math.round((correct / Math.max(1, typed.length)) * 100)} %`;
      const minutes = (performance.now() - startedAt) / 60_000;
      wpmValue.textContent = String(Math.round(typed.length / 5 / Math.max(minutes, 0.001)));
      if (typed.length >= sentence.length) finish();
    });

    const note = h('p', 'ac-note', 'Clique dans le cadre et commence a taper.');
    const again = h('button', 'ac-ghost', 'Nouvelle phrase');
    again.addEventListener('click', () => {
      sentence = randomOf(POOL);
      input.value = '';
      input.disabled = false;
      startedAt = 0;
      done = false;
      wpmValue.textContent = '0';
      accValue.textContent = '100 %';
      note.textContent = 'Clique dans le cadre et commence a taper.';
      paint();
      input.focus();
    });

    paint();
    root.append(hud, target, input, note, again);
    return () => life.stop();
  },
};
